// js/blocks/wishlist.js
(function() {
  'use strict';

  // Helper functions fallback
  function formatProductTitle(title, vendor) {
    if (typeof window.formatProductTitle === 'function') {
      return window.formatProductTitle(title, vendor);
    }
    // Fallback implementation - matches original logic
    if (!title) return "";
    // Remove everything after |
    let formattedTitle = title.split("|")[0].trim();
    // Remove vendor prefix from the beginning
    if (vendor && formattedTitle.startsWith(vendor + " ")) {
      formattedTitle = formattedTitle.substring(vendor.length + 1).trim();
    } else if (vendor && formattedTitle === vendor) {
      // If title is exactly the vendor, keep it as is
      formattedTitle = formattedTitle;
    }
    return formattedTitle;
  }

  function extractSectionId(element) {
    if (typeof window.extractSectionId === 'function') {
      return window.extractSectionId(element);
    }
    // Fallback implementation
    if (!element) return '';
    const id = element.getAttribute('id') || element.getAttribute('data-section-id');
    return id || '';
  }

  class WishlistBlock extends HTMLElement {
    constructor() {
      super();
      this._isLoaded = false;
      this._abortController = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this.loadProducts();

      // Listen for wishlist updates
      const storageKey = this.getAttribute("storage-key") || "wishlist";

      // Incremental update handler - handles add/remove without full re-render
      const incrementalUpdateHandler = async (e) => {
        const detail = e?.detail || {};
        const action = detail.action;
        const handle = detail.handle;

        // If we have specific action info, try to handle incrementally
        if (action === "remove" && handle) {
          // Find and remove the specific product card with animation
          const productCard = this.querySelector(
            `product-card[handle="${handle}"]`
          );
          if (productCard) {
            // Cancel any existing animation and animate out
            productCard.style.animation = "none";
            productCard.classList.add("product-card--removing");

            await new Promise((resolve) => setTimeout(resolve, 350));
            productCard.remove();

            // Check if wishlist is now empty
            const remainingCards = this.querySelectorAll("product-card");
            if (remainingCards.length === 0) {
              // Show empty state
              const emptyEl = this.querySelector("[data-wishlist-empty]");
              const productList = this.querySelector("product-list");
              if (productList) productList.remove();
              if (emptyEl) {
                emptyEl.style.display = "block";
              } else if (this._initialHTML) {
                // Restore initial HTML to show empty state
                this.innerHTML = this._initialHTML;
                const loadingEl = this.querySelector("[data-wishlist-loading]");
                const emptyElNew = this.querySelector("[data-wishlist-empty]");
                if (loadingEl) loadingEl.style.display = "none";
                if (emptyElNew) emptyElNew.style.display = "block";
              }

              // Hide link when empty
              const blockElement = this.closest("[data-block-id]");
              const linkEl = blockElement?.querySelector("[data-wishlist-link]");
              if (linkEl) linkEl.style.display = "none";
            }
            return; // Successfully handled incrementally
          }
        }

        // For add action or fallback, do a full reload
        // Reset state
        this._isLoaded = false;

        // Restore initial HTML if saved
        if (this._initialHTML) {
          this.innerHTML = this._initialHTML;
        }

        // Reload
        this.loadProducts();
      };

      window.addEventListener(
        "storage",
        (e) => {
          if (e.key === storageKey) {
            incrementalUpdateHandler({});
          }
        },
        { signal: this._abortController.signal }
      );

      document.addEventListener(
        "wishlist:updated",
        (e) => {
          if (!e.detail || !e.detail.key || e.detail.key === storageKey) {
            incrementalUpdateHandler(e);
          }
        },
        { signal: this._abortController.signal }
      );
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }
    }

    getWishlistHandles() {
      const storageKey = this.getAttribute("storage-key") || "wishlist";
      try {
        const data = localStorage.getItem(storageKey);
        if (!data) return [];

        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) return parsed;
        if (parsed.handles && Array.isArray(parsed.handles)) return parsed.handles;
        if (parsed.products && Array.isArray(parsed.products)) {
          return parsed.products.map((p) => p.handle || p).filter(Boolean);
        }
        return [];
      } catch (e) {
        return [];
      }
    }

    async loadProducts() {
      if (this._isLoaded) {
        return;
      }
      this._isLoaded = true;

      // Save initial HTML for reset
      if (!this._initialHTML) {
        this._initialHTML = this.innerHTML;
      }

      const handles = this.getWishlistHandles();
      const productsCount = parseInt(this.getAttribute("products-count") || "12");
      const hideProductInformation = this.hasAttribute("hide-product-information");
      const shouldReveal =
        window.themeVariables?.settings?.stagger_products_apparition || false;

      if (handles.length === 0) {
        const emptyEl = this.querySelector("[data-wishlist-empty]");
        const loadingEl = this.querySelector("[data-wishlist-loading]");
        if (loadingEl) loadingEl.style.display = "none";
        if (emptyEl) emptyEl.style.display = "block";

        // Hide link
        const blockElement = this.closest("[data-block-id]");
        const linkEl = blockElement?.querySelector("[data-wishlist-link]");
        if (linkEl) linkEl.style.display = "none";
        return;
      }

      const limitedHandles = handles.slice(0, productsCount);

      // Load products using GraphQL first (to get metafields), then fallback to ProductLoader
      const productPromises = limitedHandles.map(async (handle) => {
        // Try GraphQL first to get metafields (like ProductCard does)
        try {
          const tempCard = document.createElement("product-card");
          if (tempCard.fetchProductViaGraphQL) {
            const graphQLProduct = await tempCard.fetchProductViaGraphQL(handle);
            if (graphQLProduct) {
              return graphQLProduct;
            }
          }
        } catch (e) {
          // Continue to fallback
        }

        // Fallback to ProductLoader or JSON API
        if (typeof ProductLoader !== "undefined") {
          return ProductLoader.load(handle).catch(() => null);
        } else {
          return fetch(`${window.Shopify?.routes?.root || "/"}products/${handle}.js`)
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null);
        }
      });

      const products = (await Promise.all(productPromises)).filter(Boolean);

      if (products.length === 0) {
        const emptyEl = this.querySelector("[data-wishlist-empty]");
        const loadingEl = this.querySelector("[data-wishlist-loading]");
        if (loadingEl) loadingEl.style.display = "none";
        if (emptyEl) emptyEl.style.display = "block";

        // Hide link
        const blockElement = this.closest("[data-block-id]");
        const linkEl = blockElement?.querySelector("[data-wishlist-link]");
        if (linkEl) linkEl.style.display = "none";
        return;
      }

      // Render products using product-card-blocks snippet via AJAX
      const section = this.closest(".shopify-section");
      const block = this.closest("[data-block-id]");
      const blockId = block?.getAttribute("data-block-id") || "";

      // Create a temporary section to render product cards
      const sectionId = extractSectionId(section);
      const productHandlesQuery = products.map((p) => p.handle).join(",");

      // Use search API with handles (if supported) or render directly
      // For now, render directly using ProductCard methods
      const loadingEl = this.querySelector("[data-wishlist-loading]");
      const emptyEl = this.querySelector("[data-wishlist-empty]");

      if (loadingEl) loadingEl.style.display = "none";
      if (emptyEl) emptyEl.style.display = "none";

      // Update link visibility (link is outside wishlist-block element)
      const blockElement = this.closest("[data-block-id]");
      const linkEl = blockElement?.querySelector("[data-wishlist-link]");
      if (linkEl) {
        linkEl.style.display = products.length > 0 ? "block" : "none";
      }

      // Create product list container
      let productListHTML = "";

      products.forEach((product, index) => {
        const position = index + 1;
        const handle = product.handle || "";
        const title = formatProductTitle(product.title, product.vendor) || "";
        const url = product.url || `/products/${handle}`;

        // Get image
        let imageUrl = "";
        let imageAlt = title;

        if (product.featured_image) {
          imageUrl =
            typeof product.featured_image === "string"
              ? product.featured_image
              : product.featured_image.src || product.featured_image.url || "";
          imageAlt = product.featured_image.alt || title;
        } else if (product.images && product.images.length > 0) {
          const firstImage = product.images[0];
          imageUrl =
            typeof firstImage === "string"
              ? firstImage
              : firstImage.src || firstImage.url || "";
          imageAlt = firstImage.alt || title;
        }

        // Get price and variant info
        let price = 0;
        let compareAtPrice = null;
        const variants = product.variants || [];
        // Use variants_count if available (from GraphQL), otherwise fall back to variants array length
        const variantCount = product.variants_count || variants.length;
        const firstVariant = variants.length > 0 ? variants[0] : null;

        // Extract all product options and their values
        const productOptions = [];
        const allVariants = variants || [];
        
        if (allVariants.length > 0) {
          // Try to get options from product
          let options = product.options || product.options_with_values || [];
          
          // If options is not an array, try to infer from variant structure
          if (!Array.isArray(options) || options.length === 0) {
            const firstVar = allVariants[0];
            if (firstVar) {
              options = [];
              if (firstVar.option1) options.push({ name: 'Option 1', index: 0 });
              if (firstVar.option2) options.push({ name: 'Option 2', index: 1 });
              if (firstVar.option3) options.push({ name: 'Option 3', index: 2 });
            }
          }
          
          // Process each option
          if (Array.isArray(options) && options.length > 0) {
            options.forEach((opt, optIdx) => {
              const optionName = typeof opt === 'string' ? opt : opt.name || `Option ${optIdx + 1}`;
              const optionIndex = typeof opt === 'object' && opt.index !== undefined ? opt.index : optIdx;
              
              // Skip color option
              const optNameLower = optionName.toLowerCase();
              if (optNameLower.includes('color') || optNameLower.includes('colour') || optNameLower.includes('цвет')) {
                return; // Skip this option
              }
              
              // Collect unique values for this option
              const valueMap = new Map();
              
              allVariants.forEach(variant => {
                let optionValue = '';
                if (optionIndex === 0) {
                  optionValue = variant.option1 || '';
                } else if (optionIndex === 1) {
                  optionValue = variant.option2 || '';
                } else if (optionIndex === 2) {
                  optionValue = variant.option3 || '';
                }
                
                if (optionValue && !valueMap.has(optionValue)) {
                  // Find a variant with this value (prefer available ones)
                  const variantWithValue = allVariants.find(v => {
                    let val = '';
                    if (optionIndex === 0) val = v.option1 || '';
                    else if (optionIndex === 1) val = v.option2 || '';
                    else if (optionIndex === 2) val = v.option3 || '';
                    return val === optionValue;
                  });
                  
                  if (variantWithValue) {
                    const isAvailable = variantWithValue.available !== false && 
                                       variantWithValue.available !== 0 && 
                                       variantWithValue.availableForSale !== false;
                    valueMap.set(optionValue, { available: isAvailable });
                  }
                }
              });
              
              // Convert to array and sort if it's size
              const optionValues = Array.from(valueMap.entries()).map(([value, data]) => ({
                value: value,
                available: data.available
              }));
              
              // Sort if it's a size option
              if (optNameLower.includes('size')) {
                optionValues.sort((a, b) => {
                  const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2X', '3X', '4X'];
                  const aIndex = sizeOrder.indexOf(a.value.toUpperCase());
                  const bIndex = sizeOrder.indexOf(b.value.toUpperCase());
                  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                  if (aIndex !== -1) return -1;
                  if (bIndex !== -1) return 1;
                  return a.value.localeCompare(b.value);
                });
              } else {
                optionValues.sort((a, b) => a.value.localeCompare(b.value));
              }
              
              if (optionValues.length > 0) {
                productOptions.push({
                  name: optionName,
                  index: optionIndex,
                  values: optionValues
                });
              }
            });
          }
        }

        // Check if product has selling plans
        const hasSellingPlans =
          (product.selling_plan_groups && product.selling_plan_groups.length > 0) ||
          (product.selling_plan_groups_count &&
            product.selling_plan_groups_count > 0);

        // Check availability - handle different data formats
        let isAvailable = true;
        let hasAnyAvailableVariant = false;
        
        // Check if any variant is available
        if (allVariants.length > 0) {
          hasAnyAvailableVariant = allVariants.some(variant => {
            return variant.available !== false && 
                   variant.available !== 0 && 
                   variant.availableForSale !== false;
          });
        }
        
        // First check product-level availability (from GraphQL availableForSale or JSON API)
        if (product.available === false || product.available === 0) {
          isAvailable = false;
        } else if (variantCount === 1 && firstVariant) {
          // For single-variant products, check the variant's availability
          if (firstVariant.available === false || firstVariant.available === 0) {
            isAvailable = false;
          } else if (firstVariant.availableForSale === false) {
            isAvailable = false;
          }
        } else if (variantCount > 1) {
          // For multi-variant products, check if any variant is available
          isAvailable = hasAnyAvailableVariant;
        }

        const hasSingleVariant = variantCount === 1;

        if (firstVariant) {
          price = firstVariant.price || product.price || 0;
          compareAtPrice =
            firstVariant.compare_at_price || product.compare_at_price || null;
        } else {
          price = product.price || 0;
          compareAtPrice = product.compare_at_price || null;
        }

        // Format price using same logic as ProductCard
        const formatMoney = (cents) => {
          if (!window.themeVariables?.settings) {
            return (cents / 100).toFixed(2);
          }
          const amount = cents / 100;
          const format = window.themeVariables.settings.currencyCodeEnabled
            ? window.themeVariables.settings.moneyWithCurrencyFormat
            : window.themeVariables.settings.moneyFormat;

          // Handle all Shopify money format placeholders
          let formatted = format;

          // Replace {{amount}} with formatted amount (2 decimals)
          formatted = formatted.replace(/\{\{\s*amount\s*\}\}/g, amount.toFixed(2));

          // Replace {{amount_no_decimals}} with amount without decimals
          formatted = formatted.replace(
            /\{\{\s*amount_no_decimals\s*\}\}/g,
            Math.round(amount).toString()
          );

          // Replace {{amount_with_comma_separator}} with comma-separated amount
          formatted = formatted.replace(
            /\{\{\s*amount_with_comma_separator\s*\}\}/g,
            amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          );

          // Replace {{amount_no_decimals_with_comma_separator}}
          formatted = formatted.replace(
            /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
            Math.round(amount)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          );

          return formatted;
        };

        const formattedPrice = formatMoney(price);
        const formattedComparePrice = compareAtPrice
          ? formatMoney(compareAtPrice)
          : "";
        const isOnSale = compareAtPrice && compareAtPrice > price;

        const revealAttr = shouldReveal ? 'reveal-on-scroll="true"' : "";

        // Get collections metafield - check multiple formats
        let collectionsValue = null;
        if (product.metafields?.custom?.collections) {
          // GraphQL format: metafields.custom.collections (already extracted value)
          collectionsValue = product.metafields.custom.collections;
        } else if (product.metafields?.custom?.collections?.value) {
          // JSON API format: metafields.custom.collections.value
          collectionsValue = product.metafields.custom.collections.value;
        }

        // Build price HTML using same structure as price-list.liquid
        const priceHTML = `
          <price-list class="price-list">
            <sale-price class="${isOnSale ? "text-on-sale" : ""}">
              <span class="sr-only">Sale price</span>
              ${formattedPrice}
            </sale-price>
            ${
              isOnSale
                ? `<compare-at-price class="line-through smallcaps">
            <span class="sr-only">Regular price</span>
            ${formattedComparePrice}
          </compare-at-price>`
                : ""
            }
          </price-list>
        `;

        // Calculate stagger delay for smooth reveal
        const staggerDelay = index * 0.05; // 50ms between each card

        productListHTML += `
          <product-card class="product-card product-card--wishlist" ${revealAttr} handle="${handle}" style="animation-delay: ${staggerDelay}s">
            ${
              imageUrl
                ? `
            <div class="product-card__figure product-card__figure--wishlist">
              <a href="${url}" class="product-card__media" draggable="false" data-instant>
                <img 
                  src="${imageUrl}" 
                  alt="${title.replace(/"/g, "&quot;")}"
                  class="product-card__image product-card__image--primary aspect-portrait"
                  draggable="false"
                  onload="this.classList.add('is-loaded')"
                />
              </a>
            </div>
          `
                : ""
            }
            <div class="product-card__info">
              <div class="h-stack gap-2" style="justify-content: space-between; align-items: flex-start;">
                <div class="v-stack gap-2" style="flex: 1;">
                  ${
                    collectionsValue
                      ? `<p class="smallcaps product-card__collections-name">${collectionsValue
                          .toString()
                          .replace(/</g, "&lt;")
                          .replace(/>/g, "&gt;")}</p>`
                      : ""
                  }
                  <a href="${url}" class="product-title h5" data-instant>${title
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</a>
                  ${priceHTML}
                </div>
                <button 
                  type="button" 
                  class="wishlist-button" 
                  data-wishlist-button
                  data-product-handle="${handle}"
                  aria-label="Add to wishlist">
                  <span class="sr-only">Add to wishlist</span>
                  <svg class="wishlist-button__icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M10 17.5L8.55 16.15C4.4 12.5 1.67 10.05 1.67 7.08C1.67 4.6 3.65 2.62 6.13 2.62C7.5 2.62 8.8 3.22 9.58 4.15C10.35 3.22 11.65 2.62 13.02 2.62C15.5 2.62 17.48 4.6 17.48 7.08C17.48 10.05 14.75 12.5 10.6 16.15L10 17.5Z" 
                          stroke="currentColor" 
                          stroke-width="1.5" 
                          stroke-linecap="round" 
                          stroke-linejoin="round"
                          fill="none"
                          data-wishlist-icon-outline/>
                    <path d="M10 17.5L8.55 16.15C4.4 12.5 1.67 10.05 1.67 7.08C1.67 4.6 3.65 2.62 6.13 2.62C7.5 2.62 8.8 3.22 9.58 4.15C10.35 3.22 11.65 2.62 13.02 2.62C15.5 2.62 17.48 4.6 17.48 7.08C17.48 10.05 14.75 12.5 10.6 16.15L10 17.5Z" 
                          fill="currentColor"
                          data-wishlist-icon-fill
                          style="display: none;"/>
                  </svg>
                </button>
              </div>
              ${
                isAvailable
                  ? hasSingleVariant && !hasSellingPlans
                    ? `
            <product-form>
              <form action="${
                window.Shopify?.routes?.root || "/"
              }cart/add" method="post" enctype="multipart/form-data">
                <input type="hidden" name="on_success" value="force_open_drawer">
                <input type="hidden" name="quantity" value="1">
                <input type="hidden" name="id" value="${firstVariant.id}">
                <button type="submit" class="button button--primary">
                  Add to bag
                </button>
              </form>
            </product-form>
                  `
                    : !hasSingleVariant && productOptions.length > 0 && !hasSellingPlans
                    ? `
            <div class="wishlist-size-popup-wrapper" style="position: relative;">
              <button 
                type="button" 
                class="button button--primary wishlist-block__button heading"
                data-wishlist-add-button
                data-product-handle="${handle}"
                data-product-id="${product.id || ''}">
                Add to bag
              </button>
              <div class="wishlist-popup-overlay" data-wishlist-popup-overlay="${handle}" style="display: none;"></div>
              <div 
                class="wishlist-size-popup" 
                data-wishlist-size-popup="${handle}"
                data-product-variants="${JSON.stringify(allVariants).replace(/"/g, '&quot;')}"
                style="display: none; position: absolute; bottom: 0; left: 0; right: 0; background: white; border: 1px solid #e0e0e0; border-radius: 4px; padding: 1rem; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 100; min-width: 200px;">
                ${productOptions.map((option, optIdx) => `
                  <div class="wishlist-popup-option" style="margin-bottom: ${optIdx < productOptions.length - 1 ? '1rem' : '0'};">
                    <div class="wishlist-popup-option__title" style="font-weight: 600; margin-bottom: 0.5rem; font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em;">${option.name}</div>
                    <div class="wishlist-popup-option__values" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(50px, 1fr)); gap: 0.5rem;" data-option-index="${option.index}">
                      ${option.values.map(val => `
                        <button 
                          type="button"
                          class="wishlist-option-value ${!val.available ? 'wishlist-option-value--sold-out' : ''}"
                          data-option-name="${option.name}"
                          data-option-index="${option.index}"
                          data-option-value="${val.value}"
                          data-product-handle="${handle}"
                          ${!val.available ? 'disabled' : ''}
                          style="padding: 0.5rem; border: 1px solid #ccc; background: ${val.available ? 'white' : '#f5f5f5'}; border-radius: 4px; cursor: ${val.available ? 'pointer' : 'not-allowed'}; opacity: ${val.available ? '1' : '0.5'}; font-size: 0.875rem; transition: all 0.2s ease; text-align: center; font-weight: 500;">
                          ${val.value}
                        </button>
                      `).join('')}
                    </div>
                  </div>
                `).join('')}
                <button 
                  type="button"
                  class="wishlist-size-popup__add-button button button--primary heading"
                  data-wishlist-popup-add-button
                  data-product-handle="${handle}"
                  disabled
                  style="width: 100%; opacity: 0.5; cursor: not-allowed; margin-top: 1rem;">
                  ${window.themeVariables?.strings?.product?.general?.select_size || window.themeVariables?.strings?.product?.select_size || "Select a size"}
                </button>
              </div>
            </div>
                  `
                    : `
            <button 
              type="button" 
              aria-controls="quick-buy-${blockId}-${handle}" 
              class="button button--primary wishlist-block__button heading">
              Add to bag
            </button>
            <quick-buy-modal 
              handle="${handle}?variant=${firstVariant?.id || ""}" 
              class="quick-buy-modal modal" 
              id="quick-buy-${blockId}-${handle}">
            </quick-buy-modal>
                  `
                  : `
            <button 
              type="button" 
              class="button button--primary wishlist-block__button heading"
              disabled
              style="opacity: 0.6; cursor: not-allowed;">
              ${window.themeVariables?.strings?.out_of_stock || "Out of stock"}
            </button>
                  `
              }
            </div>
          </product-card>
        `;
      });

      const wrapperHTML = `<product-list class="product-list ${
        hideProductInformation ? "product-list--compact" : ""
      }">${productListHTML}</product-list>`;

      this.innerHTML = wrapperHTML;

      // Add wishlist button styles if not already added
      if (!document.getElementById("wishlist-button-styles")) {
        const style = document.createElement("style");
        style.id = "wishlist-button-styles";
        style.textContent = `
          .wishlist-button {
            z-index: 2;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 0;
          }
          .wishlist-button[data-in-wishlist="true"] [data-wishlist-icon-outline] {
            display: none;
          }
          .wishlist-button[data-in-wishlist="true"] [data-wishlist-icon-fill] {
            display: block !important;
          }
          .wishlist-button__icon {
            width: 20px;
            height: 20px;
            color: currentColor;
          }
          /* Wishlist product card - prevent layout shift */
          .product-card--wishlist {
            opacity: 0;
            animation: wishlistCardFadeIn 0.4s ease both;
          }
          @keyframes wishlistCardFadeIn {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          /* Removal animation */
          .product-card--removing {
            animation: wishlistCardFadeOut 0.35s ease forwards !important;
          }
          @keyframes wishlistCardFadeOut {
            from { opacity: 1; transform: scale(1); }
            to { opacity: 0; transform: scale(0.9); }
          }
          .product-card__figure--wishlist {
            position: relative;
            background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
            border-radius: 4px;
            overflow: hidden;
          }
          .product-card__figure--wishlist::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            animation: wishlistShimmer 1.5s infinite;
            z-index: 1;
          }
          @keyframes wishlistShimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .product-card__figure--wishlist .product-card__image {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .product-card__figure--wishlist .product-card__image.is-loaded {
            opacity: 1;
          }
          .product-card__figure--wishlist:has(.is-loaded)::before {
            display: none;
          }
          .product-card__figure--wishlist:has(.is-loaded) {
            background: transparent;
          }
          /* Size popup styles */
          .wishlist-size-popup-wrapper {
            position: relative;
          }
          .wishlist-popup-overlay {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 9998;
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .wishlist-popup-overlay.active {
            opacity: 1;
          }
          .wishlist-size-popup {
            min-width: 200px;
            max-width: 100%;
          }
          .wishlist-size-option {
            text-align: center;
            font-weight: 500;
          }
          .wishlist-size-option:hover:not(:disabled) {
            background: rgba(0, 0, 0, 0.05) !important;
            border-color: rgba(0, 0, 0, 0.3) !important;
          }
          .wishlist-size-option:disabled {
            cursor: not-allowed;
          }
          .wishlist-size-option.selected {
            background: rgba(0, 0, 0, 0.1) !important;
            border-color: rgba(0, 0, 0, 0.4) !important;
            font-weight: 600;
          }
          .wishlist-option-value.selected {
            background: rgba(0, 0, 0, 0.1) !important;
            border-color: rgba(0, 0, 0, 0.4) !important;
            font-weight: 600;
          }
          .wishlist-option-value:hover:not(:disabled) {
            background: rgba(0, 0, 0, 0.05) !important;
            border-color: rgba(0, 0, 0, 0.3) !important;
          }
          .wishlist-popup-option {
            margin-bottom: 1rem;
          }
          .wishlist-popup-option:last-child {
            margin-bottom: 0;
          }
          .wishlist-size-popup__title {
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          .wishlist-size-popup__add-button:not(:disabled) {
            opacity: 1 !important;
            cursor: pointer !important;
          }
          @media screen and (max-width: 700px) {
            .wishlist-size-popup-wrapper {
              position: static;
            }
            .wishlist-popup-overlay {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              z-index: 9998 !important;
            }
            .wishlist-size-popup {
              position: fixed !important;
              bottom: 0 !important;
              left: 0 !important;
              right: 0 !important;
              top: auto !important;
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
              border-radius: 16px 16px 0 0 !important;
              border-left: none !important;
              border-right: none !important;
              border-bottom: none !important;
              max-height: 90vh;
              overflow-y: auto;
              z-index: 9999 !important;
              transform: translateY(100%);
              transition: transform 0.3s ease;
            }
            .wishlist-size-popup.mobile-open {
              transform: translateY(0) !important;
            }
          }
        `;
        document.head.appendChild(style);
      }

      // Initialize size popup handlers
      const addButtons = this.querySelectorAll("[data-wishlist-add-button]");
      addButtons.forEach((button) => {
        const handle = button.getAttribute("data-product-handle");
        const popup = this.querySelector(`[data-wishlist-size-popup="${handle}"]`);
        const overlay = this.querySelector(`[data-wishlist-popup-overlay="${handle}"]`);
        
        if (!popup) return;
        
        const addToCartButton = popup.querySelector("[data-wishlist-popup-add-button]");
        const selectedOptions = {}; // Store selected values for each option
        let selectedVariantId = null;
        
        // Get variants from data attribute
        const variantsData = popup.getAttribute("data-product-variants");
        const allVariants = variantsData ? JSON.parse(variantsData.replace(/&quot;/g, '"')) : [];
        
        // Function to get total options count
        const getTotalOptions = () => {
          const optionIndices = new Set();
          popup.querySelectorAll(".wishlist-option-value[data-option-index]").forEach(btn => {
            const idx = btn.getAttribute("data-option-index");
            if (idx !== null && idx !== '') optionIndices.add(idx);
          });
          return optionIndices.size;
        };
        
        const totalOptions = getTotalOptions();
        
        // Function to find variant based on selected options
        const findVariant = () => {
          // Recalculate total options in case popup was updated
          const currentTotalOptions = getTotalOptions();
          
          // Check if all required options are selected
          const selectedCount = Object.keys(selectedOptions).length;
          if (selectedCount !== currentTotalOptions || currentTotalOptions === 0) {
            return null;
          }
          
          // Find variant that matches all selected options
          const matchingVariant = allVariants.find(variant => {
            return Object.keys(selectedOptions).every(optIndex => {
              const selectedValue = String(selectedOptions[optIndex]).trim();
              const optIdx = parseInt(optIndex);
              let variantValue = '';
              if (optIdx === 0) variantValue = String(variant.option1 || '').trim();
              else if (optIdx === 1) variantValue = String(variant.option2 || '').trim();
              else if (optIdx === 2) variantValue = String(variant.option3 || '').trim();
              return variantValue === selectedValue;
            });
          });
          
          return matchingVariant;
        };
        
        // Function to update add button state
        const updateAddButton = () => {
          const variant = findVariant();
          // Check availability - default to true unless explicitly false
          const isAvailable = variant && 
            variant.available !== false && 
            variant.available !== 0 && 
            variant.availableForSale !== false;
          
          // Get localized strings
          const selectSizeText = window.themeVariables?.strings?.product?.general?.select_size || 
                                 window.themeVariables?.strings?.product?.select_size || 
                                 "Select a size";
          const addToBagText = window.themeVariables?.strings?.product?.general?.add_to_cart_button || 
                              window.themeVariables?.strings?.add_to_bag || 
                              "Add to bag";
            
          if (isAvailable && variant) {
            selectedVariantId = variant.id;
            addToCartButton.disabled = false;
            addToCartButton.style.opacity = "1";
            addToCartButton.style.cursor = "pointer";
            addToCartButton.textContent = addToBagText;
          } else {
            selectedVariantId = null;
            addToCartButton.disabled = true;
            addToCartButton.style.opacity = "0.5";
            addToCartButton.style.cursor = "not-allowed";
            addToCartButton.textContent = selectSizeText;
          }
        };
        
        // Function to close popup
        const closePopup = () => {
          const isMobile = window.innerWidth <= 700;
          
          if (isMobile) {
            popup.classList.remove("mobile-open");
            setTimeout(() => {
              popup.style.display = "none";
              // Move popup back to wrapper on mobile after closing
              const wrapper = button.closest(".wishlist-size-popup-wrapper");
              if (wrapper && popup.parentElement === document.body) {
                wrapper.appendChild(popup);
              }
            }, 300);
          } else {
            popup.style.display = "none";
          }
          
          if (overlay && isMobile) {
            overlay.classList.remove("active");
            setTimeout(() => {
              overlay.style.display = "none";
              // Move overlay back to wrapper after closing
              const wrapper = button.closest(".wishlist-size-popup-wrapper");
              if (wrapper && overlay.parentElement === document.body) {
                wrapper.appendChild(overlay);
              }
            }, 300);
          } else if (overlay && !isMobile) {
            // On desktop, just hide overlay immediately
            overlay.style.display = "none";
            overlay.classList.remove("active");
          }
          
          // Restore body scroll
          document.body.style.overflow = "";
        };
        
        // Function to open popup
        const openPopup = () => {
          const isMobile = window.innerWidth <= 700;
          
          // Close all other popups
          document.querySelectorAll(".wishlist-size-popup").forEach(p => {
            if (p !== popup) {
              p.classList.remove("mobile-open");
              p.style.display = "none";
            }
          });
          document.querySelectorAll(".wishlist-popup-overlay").forEach(o => {
            if (o !== overlay) {
              o.classList.remove("active");
              o.style.display = "none";
              // Move overlay back to wrapper if it was moved to body
              const wrapper = o.closest(".wishlist-size-popup-wrapper") || 
                             document.querySelector(`[data-wishlist-popup-overlay="${o.getAttribute('data-wishlist-popup-overlay')}"]`)?.closest(".wishlist-size-popup-wrapper");
              if (wrapper && o.parentElement === document.body) {
                wrapper.appendChild(o);
              }
            }
          });
          
          // On mobile, move overlay and popup to body for proper z-index stacking
          if (isMobile && overlay) {
            if (overlay.parentElement !== document.body) {
              document.body.appendChild(overlay);
            }
            if (popup.parentElement !== document.body) {
              document.body.appendChild(popup);
            }
          }
          
          // Show overlay only on mobile
          if (overlay && isMobile) {
            overlay.style.display = "block";
            setTimeout(() => {
              overlay.classList.add("active");
            }, 10);
            // Prevent body scroll on mobile
            document.body.style.overflow = "hidden";
          }
          
          // Show popup
          popup.style.display = "block";
          
          // Trigger animation on mobile
          if (isMobile) {
            setTimeout(() => {
              popup.classList.add("mobile-open");
            }, 10);
          }
          
          // Reset selection
          Object.keys(selectedOptions).forEach(key => delete selectedOptions[key]);
          selectedVariantId = null;
          updateAddButton();
          popup.querySelectorAll(".wishlist-option-value").forEach(opt => {
            opt.classList.remove("selected");
          });
          
          // Adjust position if needed (desktop only)
          if (!isMobile) {
            setTimeout(() => {
              const rect = popup.getBoundingClientRect();
              const buttonRect = button.getBoundingClientRect();
              
              // Check if popup goes off top of screen
              if (rect.top < 0) {
                popup.style.bottom = "auto";
                popup.style.top = "100%";
                popup.style.marginTop = "0.5rem";
                popup.style.marginBottom = "0";
              }
              
              // Check if popup goes off right edge
              if (rect.right > window.innerWidth) {
                popup.style.right = "0";
                popup.style.left = "auto";
              }
              
              // Check if popup goes off left edge
              if (rect.left < 0) {
                popup.style.left = "0";
                popup.style.right = "auto";
              }
            }, 0);
          }
        };
        
        // Toggle popup on button click (support both click and touch)
        const handleButtonClick = function(e) {
          e.preventDefault();
          e.stopPropagation();
          
          const isVisible = popup.style.display === "block" || popup.classList.contains("mobile-open");
          
          if (!isVisible) {
            openPopup();
          } else {
            closePopup();
          }
        };
        
        button.addEventListener("click", handleButtonClick);
        button.addEventListener("touchend", function(e) {
          e.preventDefault();
          handleButtonClick(e);
        });
        
        // Close popup when clicking overlay
        if (overlay) {
          overlay.addEventListener("click", function(e) {
            e.preventDefault();
            e.stopPropagation();
            closePopup();
          });
        }
        
        // Handle window resize to adjust popup behavior
        let resizeTimeout;
        window.addEventListener("resize", () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            const isMobile = window.innerWidth <= 700;
            const isOpen = popup.style.display === "block";
            
            if (isOpen) {
              // Update the mobile-open class if needed
              if (isMobile) {
                popup.classList.add("mobile-open");
                // Show overlay on mobile if popup is open
                if (overlay) {
                  overlay.style.display = "block";
                  overlay.classList.add("active");
                  document.body.style.overflow = "hidden";
                }
              } else {
                popup.classList.remove("mobile-open");
                // Hide overlay on desktop
                if (overlay) {
                  overlay.style.display = "none";
                  overlay.classList.remove("active");
                  document.body.style.overflow = "";
                }
              }
            }
          }, 250);
        });
        
        // Handle option value selection
        const optionButtons = popup.querySelectorAll(".wishlist-option-value");
        optionButtons.forEach((optionButton) => {
          optionButton.addEventListener("click", function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            if (this.disabled) return;
            
            const optionIndex = this.getAttribute("data-option-index");
            const optionValue = this.getAttribute("data-option-value");
            
            if (!optionIndex || !optionValue) return;
            
            // Remove selected class from all buttons in the same option group
            const sameOptionButtons = popup.querySelectorAll(`[data-option-index="${optionIndex}"]`);
            sameOptionButtons.forEach(btn => btn.classList.remove("selected"));
            
            // Add selected class to clicked button
            this.classList.add("selected");
            
            // Store selected value (normalize the value)
            selectedOptions[String(optionIndex)] = String(optionValue).trim();
            
            // Update add button state
            updateAddButton();
          });
        });
        
        // Handle add to cart button click
        if (addToCartButton) {
          addToCartButton.addEventListener("click", async function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            if (!selectedVariantId || this.disabled) return;
            
            // Show loading state
            const originalText = this.textContent;
            this.disabled = true;
            this.textContent = "Adding...";
            
            // Start loading indicator
            document.documentElement.dispatchEvent(
              new CustomEvent("theme:loading:start", { bubbles: true })
            );
            
            // Prepare bundled sections
            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:prepare-bundled-sections", {
                bubbles: true,
                detail: { sections: sectionsToBundle },
              })
            );
            
            // Prepare form data like product-form does
            const formData = new FormData();
            formData.append("id", selectedVariantId);
            formData.append("quantity", "1");
            formData.append("on_success", "force_open_drawer");
            formData.append("sections", sectionsToBundle.join(","));
            formData.append("sections_url", `${window.Shopify?.routes?.root || "/"}variants/${selectedVariantId}`);
            
            try {
              // Use cart/add.js for JSON response
              const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
                method: "POST",
                body: formData,
                headers: {
                  "X-Requested-With": "XMLHttpRequest",
                },
              });
              
              // End loading indicator
              document.documentElement.dispatchEvent(
                new CustomEvent("theme:loading:end", { bubbles: true })
              );
              
              if (response.ok) {
                const responseJson = await response.json();
                
                // Get updated cart
                const cartResponse = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`);
                const cartContent = await cartResponse.json();
                cartContent["sections"] = responseJson["sections"] || {};
                
                // Dispatch variant:add event (like product-form does)
                // Create a temporary form element to dispatch the event from
                const tempForm = document.createElement("form");
                document.body.appendChild(tempForm);
                tempForm.dispatchEvent(
                  new CustomEvent("variant:add", {
                    bubbles: true,
                    detail: {
                      items: responseJson.hasOwnProperty("items")
                        ? responseJson["items"]
                        : [responseJson],
                      cart: cartContent,
                      onSuccessDo: "force_open_drawer",
                    },
                  })
                );
                document.body.removeChild(tempForm);
                
                // Dispatch cart:change event (this will open drawer automatically)
                document.documentElement.dispatchEvent(
                  new CustomEvent("cart:change", {
                    bubbles: true,
                    detail: {
                      baseEvent: "variant:add",
                      onSuccessDo: "force_open_drawer",
                      cart: cartContent,
                    },
                  })
                );
                
                // Close popup
                closePopup();
                
                // Reset button
                this.disabled = false;
                this.textContent = originalText;
              } else {
                const errorJson = await response.json();
                throw new Error(errorJson.description || "Failed to add to cart");
              }
            } catch (error) {
              console.error("Error adding to cart:", error);
              this.disabled = false;
              this.textContent = originalText;
              
              // Dispatch cart error event
              document.documentElement.dispatchEvent(
                new CustomEvent("cart:error", {
                  bubbles: true,
                  detail: {
                    error: error.message || "Failed to add product to cart",
                  },
                })
              );
              
              // Refresh cart
              document.dispatchEvent(new CustomEvent("cart:refresh"));
            }
          });
        }
      });
      
      // Close popup when clicking outside
      document.addEventListener("click", function(e) {
        if (!e.target.closest(".wishlist-size-popup-wrapper")) {
          document.querySelectorAll(".wishlist-size-popup").forEach(popup => {
            popup.style.display = "none";
          });
        }
      });

      // Initialize wishlist buttons
      const storageKey = this.getAttribute("storage-key") || "wishlist";
      const buttons = this.querySelectorAll("[data-wishlist-button]");

      const getWishlist = () => {
        try {
          const data = localStorage.getItem(storageKey);
          if (!data) return [];
          const parsed = JSON.parse(data);
          if (Array.isArray(parsed)) return parsed;
          if (parsed.handles && Array.isArray(parsed.handles))
            return parsed.handles;
          if (parsed.products && Array.isArray(parsed.products)) {
            return parsed.products.map((p) => p.handle || p).filter(Boolean);
          }
          return [];
        } catch (e) {
          return [];
        }
      };

      const saveWishlist = (handles) => {
        try {
          localStorage.setItem(storageKey, JSON.stringify(handles));
          document.dispatchEvent(
            new CustomEvent("wishlist:updated", {
              detail: { key: storageKey },
            })
          );
        } catch (e) {
          console.error("Failed to save wishlist:", e);
        }
      };

      const updateButtonState = (button, handle) => {
        const wishlist = getWishlist();
        const inWishlist = wishlist.includes(handle);
        button.setAttribute("data-in-wishlist", inWishlist);
        button.setAttribute(
          "aria-label",
          inWishlist ? "Remove from wishlist" : "Add to wishlist"
        );
      };

      // Update all buttons state
      buttons.forEach((button) => {
        const handle = button.getAttribute("data-product-handle");
        if (handle) {
          updateButtonState(button, handle);
        }
      });

      // Add click handlers
      buttons.forEach((button) => {
        button.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();

          const handle = this.getAttribute("data-product-handle");
          if (!handle) return;

          const wishlist = getWishlist();
          const index = wishlist.indexOf(handle);
          let action = "add";

          if (index > -1) {
            wishlist.splice(index, 1);
            action = "remove";
          } else {
            wishlist.push(handle);
            action = "add";
          }

          // Save wishlist with action info for incremental updates
          try {
            localStorage.setItem(storageKey, JSON.stringify(wishlist));
            document.dispatchEvent(
              new CustomEvent("wishlist:updated", {
                detail: { key: storageKey, handle: handle, action: action },
              })
            );
          } catch (err) {
            console.error("Failed to save wishlist:", err);
          }

          // Update all buttons
          buttons.forEach((btn) => {
            const btnHandle = btn.getAttribute("data-product-handle");
            if (btnHandle) {
              updateButtonState(btn, btnHandle);
            }
          });
        });
      });

      // Initialize product cards if needed
      this.dispatchEvent(
        new CustomEvent("wishlist:loaded", {
          detail: { products, blockId },
        })
      );
    }
  }

  if (!window.customElements.get("wishlist-block")) {
    window.customElements.define("wishlist-block", WishlistBlock);
  }
})();
